<template lang="pug">
ValidationObserver(ref="dialogObserver")
  AppDialog.project-dialog(
    :key="show"
    append-to-body
    :loading="showDialogLoading"
    :show="show"
    :title="isNewForm ? 'New project' : 'Edit project'"
    @close="close"
  )
    header(v-if="projectName")
      AppAvatarInitial(
        size="small"
        :text="projectName"
      )
      span {{ projectName }}
    AppTabs(
      v-if="!showDialogLoading"
      :selected-tab="selectedTab"
      compact
    )
      AppTab(
        label="General"
        tab-id="general"
        :has-error="validateTab('general')"
      )
        ValidationObserver(ref="general")
          ProjectDialogTabGeneral
      AppTab(
        v-if="membersEnabled && organization?.id"
        label="Members"
        tab-id="members"
      )
        ProjectDialogTabMembers
      AppTab(
        v-if="projectActive"
        label="Budget & Limits"
        tab-id="budget"
        :has-error="validateTab('budget')"
      )
        ProjectDialogTabBudgets(
          ref="budget"
          :validation-armed="validationArmed"
          @cleared-budget="setConfirmClearBudget"
          @member-added="setAlertDesktopAppVersion"
        )
      AppTab(
        v-if="teamsVisible"
        label="Teams"
        tab-id="teams"
        :has-error="validateTab('teams')"
      )
        ValidationObserver(ref="teams")
          ProjectDialogTabTeams
    template(#footer)
      button.btn.btn-default(@click="close") Cancel
      button.btn.btn-primary(
        :disabled="submitting"
        @click="handleSubmit"
      )
        | {{ submitButtonLabel }}
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ProjectDialogTabGeneral from './ProjectDialogTabGeneral.vue';
import ProjectDialogTabMembers from './ProjectDialogTabMembers.vue';
import ProjectDialogTabBudgets from './ProjectDialogTabBudgets.vue';
import ProjectDialogTabTeams from './ProjectDialogTabTeams.vue';
import { handleBudget } from '~/utils/budget_utils';
import { handleFormErrors } from '~/utils/form_utils';
import { initBackgroundTaskNotifications } from '~/channels/background_task_notifications_channel';

const UPDATE_CLIENT_WARNING =
  'Warning: Time tracking will not automatically stop when the member limit is reached unless the member tracking time is running the latest version of the Hubstaff application.';
const CLEAR_BUDGET_CONFIRMATION_MESSAGE =
  "Removing this project's budget will remove its budget history. Are you sure?";

export default {
  name: 'ProjectDialog',
  components: {
    ProjectDialogTabGeneral,
    ProjectDialogTabMembers,
    ProjectDialogTabBudgets,
    ProjectDialogTabTeams,
  },
  data: () => ({
    show: false,
    selectedTab: '',
    validationArmed: false,
    tabErrors: {},
    confirmClearBudget: false,
    alertDesktopAppVersion: false,
    successTimeout: null,
    backgroundTaskSubscription: null,
  }),
  computed: {
    ...mapState(['project', 'isNewForm', 'submitting', 'errors', 'budget', 'organization']),
    ...mapGetters([
      'projectData',
      'projectName',
      'teamsVisible',
      'hasMultipleProjectNames',
      'submitButtonLabel',
      'membersEnabled',
      'showDialogLoading',
      'projectActive',
    ]),
  },
  watch: {
    // Validate-on-click: the first Save arms validation; after that, re-validate on any
    // project change so the tabs/fields re-flag and clear as the user fixes them.
    project: {
      deep: true,
      handler() {
        if (this.validationArmed) {
          this.runTabValidations();
        }
      },
    },
  },
  beforeUnmount() {
    clearTimeout(this.successTimeout);
    this.successTimeout = null;
    this.unsubscribeFromBackgroundTask();
  },
  methods: {
    ...mapActions([
      'loadInitialState',
      'setFormType',
      'createProject',
      'updateProject',
      'setSubmitting',
      'resetFields',
      'updateField',
      'resetBudget',
    ]),
    open(attr) {
      this.loadData(attr);
      this.show = true;
    },
    close() {
      this.show = false;
      this.confirmClearBudget = false;
      this.alertDesktopAppVersion = false;
      this.validationArmed = false;
      this.tabErrors = {};
      this.resetFields();
    },
    loadData(attr) {
      this.setFormType(attr.newForm);
      this.loadInitialState(attr.initialState);
      this.selectedTab = attr.selectedTab ? attr.selectedTab : 'general';
    },
    // Validate-on-click: a tab is only flagged after the first Save runs runTabValidations
    // (which fills tabErrors). Every tab ref — including the budget component — exposes validate().
    validateTab(tabId) {
      return !!this.tabErrors[tabId];
    },
    // Validate every tab in one pass and record which ones failed (the dialog observer
    // doesn't aggregate the per-tab nested observers in vee-validate v4). The $nextTick
    // lets `validationArmed` propagate to the budget's member-limit `required` rules first.
    async runTabValidations() {
      await this.$nextTick();

      const tabIds = ['general', 'budget', 'teams'];
      const results = await Promise.all(tabIds.map((id) => this.$refs[id]?.validate?.() ?? true));

      this.tabErrors = Object.fromEntries(tabIds.map((id, i) => [id, !results[i]]));

      return results.every(Boolean);
    },
    async handleSubmit() {
      this.validationArmed = true;

      if (await this.runTabValidations()) {
        await this.setSubmitting(true);

        // `projectData` is a pure getter that returns a fresh clone of the store state,
        // so checkBudget/origin below can mutate the payload without touching Vuex state.
        const payload = this.projectData;

        if (this.isNewForm) {
          this.checkBudget(payload);
          this.createProject(payload).then(this.completeRequest, this.handleErrors);
        } else {
          this.showAlertForDesktopAppVersion();
          if (this.confirmClearBudget && this.project.budget && !this.project.budget.type) {
            this.submitWithClearBudgetConfirmation();
          } else {
            this.checkBudget(payload);
            payload.origin = 'update_project_modal';
            this.updateProject(payload).then(this.completeRequest, this.handleErrors);
          }
        }
      }
    },
    showAlertForDesktopAppVersion() {
      if (this.alertDesktopAppVersion) {
        alert(UPDATE_CLIENT_WARNING);
      }
    },
    submitWithClearBudgetConfirmation() {
      if (confirm(CLEAR_BUDGET_CONFIRMATION_MESSAGE)) {
        this.updateField({ path: 'budget', value: null });
        this.updateProject(this.projectData).then(this.completeRequest, this.handleErrors);
      } else {
        this.setSubmitting(false);
      }
    },
    completeRequest(response) {
      if (response.success) {
        this.handleSuccess(response);
      } else {
        this.handleErrors();
      }
    },
    handleSuccess(response) {
      let message;

      this.confirmClearBudget = false;
      this.alertDesktopAppVersion = false;
      if (this.isNewForm) {
        message = this.hasMultipleProjectNames ? 'Projects created' : 'Project created';
      } else {
        message = 'Project updated';
      }
      if (response.save_members_async) {
        message = `${message}. Members are being updated. This can take a couple of minutes. You can leave this page. We'll email you when it is complete.`;

        if (!response.background_task) {
          this.$appMessage(message);
          this.successTimeout = setTimeout(() => this.reloadProjects(), 3500);

          return;
        }

        this.subscribeToBackgroundTask(response.background_task);
        this.$appMessage(message);
        this.close();

        return;
      }
      this.$appMessage(message);
      this.successTimeout = setTimeout(() => this.reloadProjects(), 600);
    },
    handleErrors() {
      handleFormErrors(this.$refs.dialogObserver, this.errors, this.$appMessage);
      if (!this.project.budget) {
        this.resetBudget();
      }
    },
    setAlertDesktopAppVersion() {
      this.alertDesktopAppVersion = true;
    },
    setConfirmClearBudget() {
      this.confirmClearBudget = true;
    },
    checkBudget(projectData) {
      const budget = this.project.budget;
      let projectDataCheck;
      let projects;

      if (Array.isArray(projectData)) {
        projectDataCheck = projectData[0].budget;
        projects = projectData;
      } else {
        projectDataCheck = projectData.budget;
        projects = [projectData];
      }
      if (!handleBudget(this.budget, projectDataCheck, budget)) {
        projects.forEach((project) => {
          delete project.budget;
        });
      }
    },
    subscribeToBackgroundTask(backgroundTask) {
      if (!backgroundTask) { return; }

      this.unsubscribeFromBackgroundTask();
      const { subscription } = initBackgroundTaskNotifications(
        backgroundTask.organization_id,
        backgroundTask.user_id,
        backgroundTask.action,
        () => {},
        false,
        this.handleBackgroundTaskAlert,
      );

      this.backgroundTaskSubscription = subscription;
    },
    handleBackgroundTaskAlert(data) {
      if (data.type === 'notice') {
        this.$appMessage(data.message || 'Members update completed');
        this.unsubscribeFromBackgroundTask();
        this.reloadProjects();

        return;
      }

      if (data.type === 'error') {
        this.$appMessage(data.message || 'Unable to finish the members update for one or more projects.');
        this.unsubscribeFromBackgroundTask();
        this.show = true;
      }
    },
    unsubscribeFromBackgroundTask() {
      this.backgroundTaskSubscription?.unsubscribe();
      this.backgroundTaskSubscription = null;
    },
    reloadProjects() {
      if (this.$store?._actions?.loadTable) {
        this.$store.dispatch('loadTable');

        return;
      }

      window.location.reload();
    },
  },
};
</script>
